<script setup>
import {computed, ref} from "vue";
import {mdiHelpCircleOutline, mdiMagnify} from "@mdi/js";
import SelectionButtonsInput from "@/components/forms/SelectionButtonsInput.vue";
import SelectionButton from "@/components/forms/SelectionButton.vue";
import CountrySearchDialog from "@/components/hpo/identification/CountrySearchDialog.vue";
import {pgettext} from "@/utils/translate";

// Sentinel for the "cannot be determined" choice. It exists ONLY inside this
// component: the computed setter below turns it into countryUnknown = true and
// country = null, so it can never reach the mutation or the database.
const UNKNOWN = '__unknown__';

// Same four quick countries the old v-tabs offered.
const QUICK_CODES = ['CZ', 'SK', 'DE', 'UA'];

const country = defineModel('country');
const countryUnknown = defineModel('countryUnknown');

const props = defineProps({
    countries: {
        type: Array,
        required: true,
    },
    fieldProps: {
        type: Object,
        default: () => ({}),
    },
});

const searchOpen = ref(false);

const quickCountries = computed(() => {
    return QUICK_CODES
        .map((code) => props.countries.find((item) => item.code === code))
        .filter(Boolean);
});

// A country chosen from the search dialog is not in the quick list. Render it as an
// extra button so the selection is visible — otherwise picking France would leave
// nothing on screen highlighted.
const extraCountry = computed(() => {
    if (!country.value || QUICK_CODES.includes(country.value)) {
        return null;
    }
    return props.countries.find((item) => item.code === country.value)
        || {code: country.value, name: country.value};
});

// One single-select value across countries and the unknown mark. Mutual exclusion is
// structural: there is no code path that can set both.
const selection = computed({
    get() {
        if (countryUnknown.value === true) {
            return UNKNOWN;
        }
        return country.value || null;
    },
    set(value) {
        if (value === UNKNOWN) {
            country.value = null;
            countryUnknown.value = true;
        } else {
            country.value = value;
            countryUnknown.value = false;
        }
    },
});

const isUnknown = computed(() => countryUnknown.value === true);

function selectFromSearch(code) {
    selection.value = code;
}
</script>

<template>
    <div class="country-select">
        <SelectionButtonsInput v-model="selection" v-bind="fieldProps" :height="60">
            <SelectionButton v-for="item in quickCountries"
                             :key="item.code"
                             :value="item.code"
                             class="country-select__option"
                             :class="{'country-select__option--dimmed': isUnknown}">
                <strong>{{ item.name }}</strong>
            </SelectionButton>

            <SelectionButton v-if="extraCountry"
                             :value="extraCountry.code"
                             class="country-select__option"
                             :class="{'country-select__option--dimmed': isUnknown}">
                <strong>{{ extraCountry.name }}</strong>
            </SelectionButton>

            <!-- Opens the search dialog. Deliberately NOT a SelectionButton: it
                 triggers an action rather than carrying a value, so it must never
                 render as selected. -->
            <v-btn type="button"
                   variant="outlined"
                   rounded="sm"
                   :ripple="false"
                   :prepend-icon="mdiMagnify"
                   class="country-select__wide country-select__search"
                   :class="{'country-select__option--dimmed': isUnknown}"
                   @click="searchOpen = true">
                {{ pgettext('action', 'Other country…') }}
            </v-btn>

            <SelectionButton :value="UNKNOWN"
                             color="brown-darken-2"
                             class="country-select__wide country-select__unknown">
                <v-icon :icon="mdiHelpCircleOutline" class="mr-2"></v-icon>
                <strong>{{ pgettext('action', 'Cannot be determined') }}</strong>
            </SelectionButton>
        </SelectionButtonsInput>

        <p v-if="isUnknown" class="country-select__hint text-medium-emphasis">
            {{ pgettext('help', 'Recorded as nationality that could not be determined, not as blank.') }}
        </p>

        <CountrySearchDialog v-model="searchOpen"
                             :countries="countries"
                             @select="selectFromSearch"/>
    </div>
</template>

<style scoped>
/* SelectionButtonsInput lays children out with `d-flex ga-2 flex-wrap` (8px gap).
   Two per row on a phone, four from the md breakpoint. */
.country-select__option {
    flex-basis: calc(50% - 8px);
}

@media (min-width: 960px) {
    .country-select__option {
        flex-basis: calc(25% - 8px);
    }
}

/* !important is needed to beat Vuetify's .flex-grow-1 utility, which
   SelectionButton hardcodes on its root. */
.country-select__wide {
    flex: 1 1 100% !important;
}

.country-select__search {
    height: 52px !important;
    margin-top: 8px;
    border-style: dashed;
}

/* Sits apart and lower in the hierarchy than the countries, so it is visible
   without being easy to hit by accident. */
.country-select__unknown {
    height: 52px !important;
    margin-top: 16px;
}

/* Dimmed but still tappable while "cannot be determined" is active, so the
   choice stays reversible. */
.country-select__option--dimmed {
    opacity: 0.42;
}

.country-select__hint {
    margin-top: 4px;
    font-size: 0.8125rem;
    line-height: 1.4;
    white-space: normal;
}
</style>
